import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Award, Download, Loader2, RefreshCw, ShieldCheck, ShieldOff } from "lucide-react";
import { adminListCertificateAudit, adminListCertificates, reissueCertificateSignature } from "@/lib/api/certificates.functions";
import { downloadCertificatePdf, type CertificateTemplate } from "@/lib/certificate-pdf";

const TEMPLATES: { id: CertificateTemplate; label: string; blurb: string }[] = [
  { id: "classic", label: "Classic Navy + Gold", blurb: "Traditional, formal. Matches NDLCS brand." },
  { id: "modern", label: "Modern Charcoal + Emerald", blurb: "Clean, contemporary, Microsoft-track friendly." },
  { id: "elegant", label: "Elegant Plum + Champagne", blurb: "Ceremonial, premium feel for senior credentials." },
  { id: "royal-gold", label: "Royal Gold", blurb: "Deep navy with rich 24-carat gold ornaments." },
  { id: "crimson-shield", label: "Crimson Shield", blurb: "Heraldic red & gold with a bold shield emblem." },
  { id: "obsidian", label: "Obsidian", blurb: "Black & champagne — premium, executive." },
  { id: "emerald-laurel", label: "Emerald Laurel", blurb: "Forest green with antique-gold laurel." },
  { id: "sapphire", label: "Sapphire", blurb: "Deep blue institutional finish." },
  { id: "bronze-heritage", label: "Bronze Heritage", blurb: "Warm bronze, classical academy feel." },
  { id: "midnight-platinum", label: "Midnight Platinum", blurb: "Slate navy with cool platinum metalwork." },
];

const TRACK_LABEL: Record<string, string> = {
  writing: "Legal Writing Academy",
  research: "Legal Research Hub",
  court_training: "Court Online Training Centre",
  tutoring: "LLB Tutoring Centre",
};

export const Route = createFileRoute("/_authenticated/portal/admin/certificates")({
  component: AdminCertificatesPage,
});

function AdminCertificatesPage() {
  const fetchAll = useServerFn(adminListCertificates);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-certificates"],
    queryFn: () => fetchAll(),
  });
  const certs = data?.certificates ?? [];

  return (
    <section className="space-y-10">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin</p>
        <h2 className="mt-1 font-display text-2xl text-navy">Certificates</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Personalise and preview certificate templates, then view every certificate issued to students.
        </p>
      </header>

      <CertificateDesigner />

      <div>
        <h3 className="font-display text-lg text-navy">All issued certificates</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          {certs.length} certificate{certs.length === 1 ? "" : "s"} on record
        </p>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {!isLoading && certs.length === 0 && (
          <div className="rounded-md border border-dashed border-input p-6 text-center text-sm text-muted-foreground">
            <Award className="mx-auto h-8 w-8 text-gold" />
            No certificates have been issued yet.
          </div>
        )}

        <ul className="space-y-2">
          {certs.map((c: any) => (
            <AdminCertRow key={c.id} cert={c} />
          ))}
        </ul>
      </div>

      <AuditLogPanel />
    </section>
  );
}

function AuditLogPanel() {
  const fetchAudit = useServerFn(adminListCertificateAudit);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-cert-audit"],
    queryFn: () => fetchAudit({ data: {} }),
  });
  const events: any[] = data?.events ?? [];
  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg text-navy">Certificate audit log</h3>
          <p className="text-xs text-muted-foreground">
            Every issuance, re-sign, download, and revocation. Tamper-evident by design — entries are append-only and admin-readable.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-3 py-1.5 text-xs hover:border-navy"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {isLoading && (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {!isLoading && events.length === 0 && (
        <p className="mt-3 rounded-md border border-dashed border-input p-4 text-xs text-muted-foreground">
          No audit events yet.
        </p>
      )}

      <ul className="mt-3 space-y-1.5">
        {events.map((e) => (
          <li key={e.id} className="rounded-md border border-input bg-card p-2.5 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-navy/10 px-1.5 py-0.5 font-semibold uppercase tracking-wider text-navy">
                {e.action}
              </span>
              <span className="text-muted-foreground">
                {new Date(e.created_at).toLocaleString("en-ZA")}
              </span>
              {e.actor_email && (
                <span className="text-muted-foreground">· {e.actor_email}</span>
              )}
              {e.actor_role && (
                <span className="text-muted-foreground">({e.actor_role})</span>
              )}
            </div>
            <p className="mt-1 truncate font-mono text-[10px] text-muted-foreground">
              cert {e.certificate_id}
              {e.document_hash ? ` · sha256:${e.document_hash.slice(0, 16)}…` : ""}
              {e.signature ? ` · sig:${e.signature.slice(0, 16)}…` : ""}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CertificateDesigner() {
  const [template, setTemplate] = useState<CertificateTemplate>("classic");
  const [recipient, setRecipient] = useState("Thandi Mthembu");
  const [title, setTitle] = useState("Certificate of Completion — Microsoft Azure Fundamentals");
  const [trackLabel, setTrackLabel] = useState("NDLCS Academy — Microsoft Learn Track");
  const [busy, setBusy] = useState(false);

  async function preview() {
    setBusy(true);
    try {
      await downloadCertificatePdf({
        verificationCode: "SAMPLE-PREVIEW",
        recipientName: recipient || "Sample Student",
        title: title || "Sample Certificate",
        trackLabel: trackLabel || "NDLCS Academy",
        issuedAt: new Date().toISOString(),
        trackRef: "sample",
        template,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
      <h3 className="font-display text-lg text-navy">Certificate designer · sample preview</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Pick a template, personalise the fields, and download a sample PDF. This preview is watermarked
        with verification code <span className="font-mono">SAMPLE-PREVIEW</span> and is NOT recorded as an issued certificate.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTemplate(t.id)}
            className={`rounded-lg border p-3 text-left text-xs transition ${
              template === t.id ? "border-navy bg-navy/5 ring-2 ring-navy" : "border-input hover:border-navy"
            }`}
          >
            <div className="mb-2 flex h-12 items-center justify-center rounded text-[10px] font-bold uppercase tracking-widest text-white" style={swatchStyle(t.id)}>
              NDLCS
            </div>
            <p className="font-semibold text-navy">{t.label}</p>
            <p className="mt-0.5 text-muted-foreground">{t.blurb}</p>
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="text-xs">
          <span className="font-semibold text-navy">Recipient name</span>
          <input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs">
          <span className="font-semibold text-navy">Track / division label</span>
          <input
            value={trackLabel}
            onChange={(e) => setTrackLabel(e.target.value)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs sm:col-span-2">
          <span className="font-semibold text-navy">Certificate title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </label>
      </div>

      <button
        onClick={preview}
        disabled={busy}
        className="mt-4 inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        Download sample PDF
      </button>
    </div>
  );
}

function swatchStyle(t: CertificateTemplate): React.CSSProperties {
  const map: Record<CertificateTemplate, string> = {
    classic: "linear-gradient(135deg, rgb(15,27,61), rgb(176,141,87))",
    modern: "linear-gradient(135deg, rgb(17,24,39), rgb(16,185,129))",
    elegant: "linear-gradient(135deg, rgb(60,25,60), rgb(201,161,74))",
    "royal-gold": "linear-gradient(135deg, rgb(12,23,56), rgb(212,175,55))",
    "crimson-shield": "linear-gradient(135deg, rgb(120,18,30), rgb(212,175,55))",
    obsidian: "linear-gradient(135deg, rgb(10,10,14), rgb(196,173,122))",
    "emerald-laurel": "linear-gradient(135deg, rgb(12,60,45), rgb(200,168,90))",
    sapphire: "linear-gradient(135deg, rgb(16,36,92), rgb(192,160,95))",
    "bronze-heritage": "linear-gradient(135deg, rgb(56,36,18), rgb(180,130,70))",
    "midnight-platinum": "linear-gradient(135deg, rgb(22,28,45), rgb(188,192,200))",
  };
  return { background: map[t] ?? map.classic };
}

function AdminCertRow({ cert }: { cert: any }) {
  const [busy, setBusy] = useState(false);
  const [signing, setSigning] = useState(false);
  const [sig, setSig] = useState<string | null>(cert.signature ?? null);
  const [template, setTemplate] = useState<CertificateTemplate>("classic");
  const revoked = !!cert.revoked_at;
  const reissue = useServerFn(reissueCertificateSignature);

  async function download() {
    setBusy(true);
    try {
      await downloadCertificatePdf({
        verificationCode: cert.verification_code,
        recipientName: cert.recipient_name,
        trackLabel: TRACK_LABEL[cert.track] ?? cert.track,
        title: cert.title,
        issuedAt: cert.issued_at,
        trackRef: cert.track_ref,
        template,
        signature: sig,
        certificateId: cert.id,
      });
    } finally {
      setBusy(false);
    }
  }

  async function resign() {
    setSigning(true);
    try {
      const r = await reissue({ data: { id: cert.id } });
      setSig(r.signature);
    } finally {
      setSigning(false);
    }
  }

  return (
    <li className="rounded-lg border border-input bg-card p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-navy">{cert.title}</p>
          <p className="truncate text-xs text-muted-foreground">
            {cert.recipient_name} · {TRACK_LABEL[cert.track] ?? cert.track} ·{" "}
            <span className="font-mono">{cert.verification_code}</span> ·{" "}
            {new Date(cert.issued_at).toLocaleDateString("en-ZA")}
          </p>
          <p className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3 text-emerald-600" />
            {sig ? <span className="font-mono">sig {sig.slice(0, 16)}…</span> : <span className="text-rose-600">unsigned</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={template}
            onChange={(e) => setTemplate(e.target.value as CertificateTemplate)}
            className="rounded-md border border-input bg-background px-2 py-1 text-xs"
          >
            {TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <button
            onClick={resign}
            disabled={signing}
            className="inline-flex items-center gap-1 rounded-md border border-input bg-card px-2.5 py-1.5 text-xs hover:border-navy disabled:opacity-60"
            title="Re-sign with the current signing key"
          >
            {signing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Re-sign
          </button>
          {revoked ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700">
              <ShieldOff className="h-3 w-3" /> Revoked
            </span>
          ) : (
            <button
              onClick={download}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
              PDF
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
